import { Injectable, signal, computed } from '@angular/core';

export interface ComparisonItem {
  id: string;
  type: 'service' | 'resource';
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class ComparisonService {
  private readonly MAX_ITEMS = 4;
  
  // Signal para armazenar itens selecionados
  private selectedItems = signal<ComparisonItem[]>([]);
  
  // Computed signals
  items = computed(() => this.selectedItems());
  count = computed(() => this.selectedItems().length);
  canCompare = computed(() => this.selectedItems().length >= 2);
  isFull = computed(() => this.selectedItems().length >= this.MAX_ITEMS);
  
  /**
   * Adiciona um item à comparação
   * Retorna true se adicionado com sucesso, false se já está no limite
   */
  addItem(item: ComparisonItem): boolean {
    if (this.isFull()) {
      return false;
    }
    
    const current = this.selectedItems();
    if (!current.find(i => i.id === item.id)) {
      this.selectedItems.set([...current, item]);
      return true;
    }
    
    return false;
  }
  
  /**
   * Remove um item da comparação
   */
  removeItem(id: string): void {
    const current = this.selectedItems();
    this.selectedItems.set(current.filter(item => item.id !== id));
  }
  
  /**
   * Verifica se um item está selecionado
   */
  isSelected(id: string): boolean {
    return this.selectedItems().some(item => item.id === id);
  }
  
  /**
   * Alterna a seleção de um item
   */
  toggleItem(item: ComparisonItem): boolean {
    if (this.isSelected(item.id)) {
      this.removeItem(item.id);
      return false;
    } else {
      return this.addItem(item);
    }
  }
  
  /**
   * Limpa todos os itens selecionados
   */
  clear(): void {
    this.selectedItems.set([]);
  }
  
  /**
   * Retorna o número máximo de itens permitidos
   */
  getMaxItems(): number {
    return this.MAX_ITEMS;
  }
}
